import { type FC } from "react";

import {
  type AssessmentCountDataWithNotSubmitted,
  useAssessmentMode,
  useAssessmentPublishActions,
} from "../../../../utils/assessment";

import { SECONDARY_OVERLAY_QUERY_PARAM, SecondaryOverlay } from "components/constants";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";

interface AssessmentGradingLastPanelProps {
  onBack: () => void;
  page: PageType;
  assessedCountData: AssessmentCountDataWithNotSubmitted;
  hideButtons?: boolean;
}

// This is the last panel in the assessment grading workflow. It can only be accessed
// if the assessments haven't been published yet.
export const AssessmentGradingLastPanel: FC<AssessmentGradingLastPanelProps> = ({
  onBack,
  page,
  assessedCountData: { count, total, notSubmittedCount },
  hideButtons = false,
}) => {
  const { publishGrades } = useAssessmentPublishActions(page);
  const { stopAssessing } = useAssessmentMode(page);

  const handlePublishAndExit = async () => {
    await publishGrades();
    await stopAssessing({ [SECONDARY_OVERLAY_QUERY_PARAM]: SecondaryOverlay.AssessmentSummary });
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4">
      <div className="text-sm font-normal leading-[1.3125rem]">
        {page.assessments_published_at
          ? t("submission_assessment.last_panel_description_published", {
              context: count === total ? "all" : "some",
              count,
              total,
              notSubmitted: notSubmittedCount,
            })
          : t("submission_assessment.last_panel_description", {
              context: count === total ? "all" : "some",
              count,
              total,
              notSubmitted: notSubmittedCount,
            })}
      </div>

      {!hideButtons && (
        <>
          <Button
            className="w-full"
            eventName="Assessment - Publish"
            kind="greenPrimary"
            size="s"
            onClick={handlePublishAndExit}
          >
            {t("assessment.publish_page_assessments")}
          </Button>

          <Button className="w-full" kind="white" size="s" onClick={onBack}>
            {t("assessment.edit_assessments")}
          </Button>

          <Button
            className="w-full"
            kind="white"
            size="s"
            onClick={() => {
              stopAssessing();
            }}
          >
            {t("assessment.finish_later")}
          </Button>
        </>
      )}
    </div>
  );
};
